import { DEFAULT_TRAINING_DESIGN } from '../schemas/training.js';
/**
 * Curated looks for a training's design, adapted from the style presets of frontend-slides
 * (github.com/zarazhangrui/frontend-slides). Choosing one fills in the design's fonts, colors
 * and slide style; every field stays editable afterwards, and the design remembers no preset —
 * a design matching one exactly is simply shown as that preset.
 *
 * Colors follow the design's roles: titles in primary, bullets and decoration in accent, so
 * each accent here is picked to read against its background. Dark presets set primary to a
 * light color — the ground slides (title, closing) of the signal style use the accent instead.
 */
export const PRESET_DIRECTIONS = ['corporate', 'bold', 'editorial', 'playful'];
export const PRESET_DIRECTION_LABEL = {
    corporate: 'Clean corporate',
    bold: 'Bold / dark',
    editorial: 'Elegant editorial',
    playful: 'Friendly / playful',
};
export const DESIGN_PRESETS = [
    {
        id: 'classic',
        name: 'Classic',
        direction: 'corporate',
        description: 'Navy and gold, no decoration — the content carries the slide.',
        design: {
            headingFont: DEFAULT_TRAINING_DESIGN.headingFont,
            bodyFont: DEFAULT_TRAINING_DESIGN.bodyFont,
            primaryColor: DEFAULT_TRAINING_DESIGN.primaryColor,
            accentColor: DEFAULT_TRAINING_DESIGN.accentColor,
            backgroundColor: DEFAULT_TRAINING_DESIGN.backgroundColor,
            textColor: DEFAULT_TRAINING_DESIGN.textColor,
            style: 'minimal',
        },
    },
    {
        id: 'swiss-modern',
        name: 'Swiss Modern',
        direction: 'corporate',
        description: 'White, black and a single red, on rules and a strict grid.',
        design: {
            headingFont: 'Archivo',
            bodyFont: 'Nunito',
            primaryColor: '#111111',
            accentColor: '#ff3300',
            backgroundColor: '#ffffff',
            textColor: '#1a1a1a',
            style: 'swiss',
        },
    },
    {
        id: 'electric-studio',
        name: 'Electric Studio',
        direction: 'corporate',
        description: 'Near-black type on white with an electric blue accent.',
        design: {
            headingFont: 'Manrope',
            bodyFont: 'Manrope',
            primaryColor: '#0a0a0a',
            accentColor: '#4361ee',
            backgroundColor: '#ffffff',
            textColor: '#1f1f1f',
            style: 'swiss',
        },
    },
    {
        id: 'bold-signal',
        name: 'Bold Signal',
        direction: 'bold',
        description: 'Charcoal with an orange focal card and large section numbers.',
        design: {
            headingFont: 'Archivo',
            bodyFont: 'Space Grotesk',
            primaryColor: '#ffffff',
            accentColor: '#ff5722',
            backgroundColor: '#1a1a1a',
            textColor: '#e6e6e6',
            style: 'signal',
        },
    },
    {
        id: 'terminal-green',
        name: 'Terminal Green',
        direction: 'bold',
        description: 'Monospace on a dark terminal, for technical trainings.',
        design: {
            headingFont: 'JetBrains Mono',
            bodyFont: 'JetBrains Mono',
            primaryColor: '#e6edf3',
            accentColor: '#39d353',
            backgroundColor: '#0d1117',
            textColor: '#c9d1d9',
            style: 'signal',
        },
    },
    {
        id: 'creative-voltage',
        name: 'Creative Voltage',
        direction: 'bold',
        description: 'Deep navy with neon yellow, for energetic sessions.',
        design: {
            headingFont: 'Syne',
            bodyFont: 'Space Mono',
            primaryColor: '#ffffff',
            accentColor: '#d4ff00',
            backgroundColor: '#1a1a2e',
            textColor: '#e8e8f0',
            style: 'signal',
        },
    },
    {
        id: 'dark-botanical',
        name: 'Dark Botanical',
        direction: 'editorial',
        description: 'Serif display on near-black, with warm gold hairlines.',
        design: {
            headingFont: 'Cormorant',
            bodyFont: 'IBM Plex Sans',
            primaryColor: '#e8e4df',
            accentColor: '#d4a574',
            backgroundColor: '#0f0f0f',
            textColor: '#c9c4bd',
            style: 'editorial',
        },
    },
    {
        id: 'vintage-editorial',
        name: 'Vintage Editorial',
        direction: 'editorial',
        description: 'Cream paper, a characterful serif and terracotta accents.',
        design: {
            headingFont: 'Fraunces',
            bodyFont: 'Work Sans',
            primaryColor: '#1a1a1a',
            accentColor: '#b5562f',
            backgroundColor: '#f5f3ee',
            textColor: '#3a3a3a',
            style: 'editorial',
        },
    },
    {
        id: 'paper-ink',
        name: 'Paper & Ink',
        direction: 'editorial',
        description: 'Literary: charcoal serif on cream with a crimson accent.',
        design: {
            headingFont: 'Cormorant Garamond',
            bodyFont: 'Source Serif 4',
            primaryColor: '#1a1a1a',
            accentColor: '#c41e3a',
            backgroundColor: '#faf9f7',
            textColor: '#2b2b2b',
            style: 'editorial',
        },
    },
    {
        id: 'pastel-geometry',
        name: 'Pastel Geometry',
        direction: 'playful',
        description: 'Soft blue ground, rounded cards and a column of pastel pills.',
        design: {
            headingFont: 'Plus Jakarta Sans',
            bodyFont: 'Plus Jakarta Sans',
            primaryColor: '#2f3a4a',
            accentColor: '#7c6aad',
            backgroundColor: '#e3ebf2',
            textColor: '#2f3a4a',
            style: 'soft',
        },
    },
    {
        id: 'split-pastel',
        name: 'Split Pastel',
        direction: 'playful',
        description: 'Peach and lavender, rounded and friendly.',
        design: {
            headingFont: 'Outfit',
            bodyFont: 'Outfit',
            primaryColor: '#1a1a1a',
            accentColor: '#d0607e',
            backgroundColor: '#f7ebe3',
            textColor: '#2a2a2a',
            style: 'soft',
        },
    },
];
/** The preset `design` matches exactly (ignoring otherDetails), or null for a custom design. */
export function matchingPreset(design) {
    const keys = Object.keys(DESIGN_PRESETS[0].design);
    return (DESIGN_PRESETS.find((p) => keys.every((k) => String(p.design[k]).toLowerCase() === String(design[k] ?? 'minimal').toLowerCase())) ?? null);
}
/** `design` with a preset's fonts, colors and style, keeping its free-text details. */
export function applyPreset(design, preset) {
    return { ...preset.design, otherDetails: design.otherDetails };
}
//# sourceMappingURL=presets.js.map